import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

// OPS-110 follow-up: default URL for the standalone Catalog Graph page.
//
// app-config.yaml's `page:catalog-graph: config: rootEntityRefs:` block
// (landed in PR #30) is silently ignored at runtime — likely a side
// effect of the frontend-extension API migration (config.schema →
// configSchema, per the deprecation warning visible in the production
// console). The page itself works correctly when rootEntityRefs is
// supplied via URL params, so we override the sidebar nav href to
// produce that URL directly. Entity-page "View graph" deep links
// supply their own rootEntityRefs and are unaffected.
//
// group:default/edgebeam is the org root entity authored by OPS-103.
// Brackets/colons/slashes are URL-encoded for cross-router safety.
const CATALOG_GRAPH_ANCHORED_HREF =
  '/catalog-graph?rootEntityRefs%5B%5D=group%3Adefault%2Fedgebeam';

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => {
        const href =
          item.href === '/catalog-graph'
            ? CATALOG_GRAPH_ANCHORED_HREF
            : item.href;
        return (
          <SidebarItem icon={() => item.icon} to={href} text={item.title} />
        );
      });

      // Skipped items
      nav.take('page:search'); // Using search modal instead
      // page:user-settings is rendered manually below as the avatar
      // SidebarItem (icon = UserSettingsSignInAvatar). Take it here so
      // it doesn't double-render via nav.rest().
      nav.take('page:user-settings');

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {nav.take('page:catalog')}
            {nav.take('page:scaffolder')}
            <SidebarDivider />
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          {nav.take('page:app-visualizer')}
          {/*
            OPS-95 follow-up: avatar rendered as a dedicated
            SidebarItem instead of a SidebarGroup icon. SidebarGroup
            only draws its own icon in collapsed mode; with the
            avatar wrapped in a group, expanded mode showed the
            group's children flat and the avatar disappeared. Using
            a SidebarItem renders the avatar at standard icon size
            in both modes. Replaces the page:user-settings entry
            (taken at the top of the component).
          */}
          <SidebarItem
            icon={() => <UserSettingsSignInAvatar />}
            text="Settings"
            to="/settings"
          />
        </Sidebar>
      );
    },
  },
});
